import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BookOpen, ChevronRight, ShoppingCart } from 'lucide-react'
import { BookCover } from '@/components/ui/BookCard'
import { useBookInstructor } from '@/lib/useBookInstructor'
import { useCart } from '@/services/cartService'
import { useAuthStore } from '@/store/authStore'
import { getBookBySlug, bookDiscountPct, type ApiBookDetail, type BookDetailContent } from '@/services/bookService'

// หน้ารายละเอียดหนังสือ — ข้อมูลจาก API (GET /api/books/<slug>) เฉพาะหนังสือที่เผยแพร่แล้ว
// ดาวสี่แฉกเล็กๆ รอบปกหนังสือ (ตกแต่งเท่านั้น)
function Sparkle({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={`pointer-events-none absolute text-accent ${className}`} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <path d="M12 1.5c.8 6.2 4.3 9.7 10.5 10.5C16.3 12.8 12.8 16.3 12 22.5 11.2 16.3 7.7 12.8 1.5 12 7.7 11.2 11.2 7.7 12 1.5Z" />
    </svg>
  )
}

export default function BookDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  // ผลลัพธ์เก็บคู่กับ slug ที่ขอ — slug เปลี่ยนแล้วผลเดิมไม่ตรง = กำลังโหลด
  const [result, setResult] = useState<{ slug: string; book: ApiBookDetail | null; failed: 'notfound' | 'error' | null } | null>(null)
  const [activeImage, setActiveImage] = useState(0)
  const cart = useCart()
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn)
  const [cartNotice, setCartNotice] = useState('')

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    getBookBySlug(slug)
      .then((b) => { if (!cancelled) setResult({ slug, book: b, failed: null }) })
      .catch((err) => {
        if (cancelled) return
        // backend ตอบ 404 = ไม่พบ/ยังไม่เผยแพร่ — อย่างอื่นถือว่าโหลดไม่สำเร็จ
        setResult({ slug, book: null, failed: err instanceof Error && err.message.includes('ไม่พบ') ? 'notfound' : 'error' })
      })
    return () => { cancelled = true }
  }, [slug])

  const loaded = Boolean(slug) && result?.slug === slug
  const book = loaded ? result?.book ?? null : null
  const status: 'loading' | 'ready' | 'notfound' | 'error' = !slug ? 'notfound' : !loaded ? 'loading' : result?.failed ?? 'ready'
  const instructor = useBookInstructor(book?.instructor_id)

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 pt-20 text-center">
        <p className="text-sm text-text-muted">กำลังโหลดหนังสือ...</p>
      </div>
    )
  }

  if (!book) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pt-20 text-center">
        <p className="text-lg font-medium text-text">{status === 'error' ? 'โหลดหนังสือไม่สำเร็จ' : 'ไม่พบหนังสือเล่มนี้'}</p>
        <Link to="/books" className="mt-3 text-sm text-accent hover:underline">← กลับไปหน้าหนังสือทั้งหมด</Link>
      </div>
    )
  }

  const discount = bookDiscountPct(book)
  const gallery = [book.cover_url, ...(book.images ?? [])].filter((x): x is string => Boolean(x))
  const shownImage = activeImage < gallery.length ? activeImage : 0   // เปลี่ยนเล่มแล้ว index เดิมอาจเกินจำนวนรูปของเล่มใหม่

  // สั่งซื้อ = ไปหน้าชำระเงินเลย (ชิ้นนี้ชิ้นเดียว ไม่ผ่านตะกร้า) · ปุ่ม "เพิ่มลงตะกร้า" แค่ใส่ตะกร้า ไม่พาไปไหน
  const buy = () => {
    if (!isLoggedIn) return navigate('/login', { state: { from: { pathname: `/books/${book.slug}` } } })
    navigate('/checkout/books', {
      state: { direct: { kind: 'book', refId: book.book_id, quantity: 1, title: book.title, image: book.cover_url, subtitle: book.instructor_name || book.author_name } },
    })
  }
  const inCart = cart.has('book', book.book_id)
  const addToCart = () => {
    if (!isLoggedIn) return navigate('/login', { state: { from: { pathname: `/books/${book.slug}` } } })
    void cart.add({ kind: 'book', refId: book.book_id, quantity: 1 }).then((res) => setCartNotice(res.ok ? 'เพิ่มลงตะกร้าแล้ว' : res.error))
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ── HERO: พื้นหลังภาพประกอบโลจิสติกส์โทนม่วงอ่อน (public/media/books-hero-bg.webp) / ชื่อ → คำอธิบาย → ผู้สอน → ราคา+ปุ่มซื้อ ── */}
      <section
        className="relative overflow-hidden border-b border-border bg-[#F8F7FF] bg-cover bg-center pb-20 pt-28"
        style={{ backgroundImage: "url('/media/books-hero-bg.webp')" }}
      >
        <div className="relative mx-auto max-w-6xl px-4 md:px-6">
          <nav className="mb-10 flex items-center gap-1.5 text-xs text-text-muted">
            <Link to="/books" className="hover:text-accent">หนังสือ</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/books?cat=${book.category_id}`} className="hover:text-accent">{book.category_name}</Link>
          </nav>

          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-[minmax(0,240px)_1fr] md:gap-16">
            {/* ปก */}
            <div className="mx-auto w-full max-w-[200px] md:mx-0 md:max-w-none">
              <div className="relative">
                {/* แท่นเงาใต้หนังสือ — วงรีสีม่วงอ่อนกว้างกว่าปก เอียงตามปกเล็กน้อย + เงาเข้มตรงใต้ขอบล่างปก */}
                <div aria-hidden className="pointer-events-none absolute -bottom-9 left-1/2 h-20 w-[150%] -translate-x-1/2 -rotate-3 rounded-[50%] bg-gradient-to-b from-[#CFC9F7]/80 to-[#E4E0FB]/70 blur-[3px]" />
                <div aria-hidden className="pointer-events-none absolute -bottom-6 left-1/2 h-10 w-[105%] -translate-x-1/2 -rotate-3 rounded-[50%] bg-accent/35 blur-lg" />
                {/* ขีดวิ้ง 3 ขีด — แผ่ออกจากมุมขวาบนของปกเป็นพัด (มุม 75°/45°/15°) ความยาวเท่ากัน เว้นระยะห่างจากมุมเท่ากัน */}
                <svg viewBox="0 0 40 60" aria-hidden className="pointer-events-none absolute h-14 w-10 text-accent" style={{ left: 'calc(100% - 6px)', bottom: 'calc(100% - 2px)' }} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M7.1 44.4 L11.8 27" /><path d="M12.5 47.5 L25.2 34.8" /><path d="M15.6 52.9 L33 48.2" />
                </svg>
                <Sparkle className="-right-8 bottom-10 h-5 w-5" />
                <Sparkle className="-left-7 bottom-1/3 h-4 w-4 opacity-70" />
                <Sparkle className="-left-3 -top-6 h-3.5 w-3.5 opacity-60" />
              <div className="relative -rotate-3 transition-transform duration-300 hover:rotate-0">
                <div className="relative overflow-hidden rounded-sm border border-white bg-white shadow-[0_22px_44px_rgba(76,63,181,0.25)]">
                  {gallery.length > 0
                    ? <img src={gallery[shownImage]} alt={book.title} className="aspect-[3/4] w-full object-cover" />
                    : <BookCover book={book} />}
                </div>
              </div>
              </div>
              {gallery.length > 1 && (
                <div className="mt-6 flex justify-center gap-2 md:justify-start">
                  {gallery.map((src, i) => (
                    <button
                      key={src}
                      onClick={() => setActiveImage(i)}
                      className={`h-16 w-12 overflow-hidden rounded-sm border-2 ${i === shownImage ? 'border-accent' : 'border-border'}`}
                    >
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ข้อมูล + ซื้อ */}
            <div className="max-w-xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-block rounded-full bg-accent/10 px-3 py-1 text-[11px] font-medium text-accent">{book.category_name}</span>
                {book.page_count ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white/70 px-3 py-1 text-[11px] font-medium text-text-muted">
                    <BookOpen className="h-3 w-3" />
                    {book.page_count.toLocaleString('th-TH')} หน้า
                  </span>
                ) : null}
              </div>
              <h1 className="mt-3 font-display text-2xl font-bold leading-snug text-text sm:text-[28px]">{book.title}</h1>
              <p className="mt-3 max-w-xl text-sm leading-7 text-text-muted">{book.short_description}</p>

              {/* ผู้สอน — รูปวงกลม + ชื่อ ไม่มีกรอบครอบ กดไปหน้าผู้สอน */}
              {instructor ? (
                <Link to={`/instructors/${instructor.instructor_id}`} className="group mt-5 inline-flex max-w-xl items-center gap-3">
                  <img
                    src={instructor.avatar_url}
                    alt={instructor.name}
                    className="h-10 w-10 shrink-0 rounded-full bg-white object-cover shadow-sm ring-2 ring-accent/20 transition group-hover:ring-accent/50"
                  />
                  <div className="min-w-0">
                    <p className="text-[11px] leading-none text-text-muted">ผู้สอน</p>
                    <p className="mt-1 text-sm font-semibold leading-none text-text transition-colors group-hover:text-accent">{instructor.name} <span className="inline-block transition-transform group-hover:translate-x-1">→</span></p>
                    {instructor.bio && <p className="mt-1 text-xs leading-snug text-text-muted">{instructor.bio}</p>}
                  </div>
                </Link>
              ) : (
                <p className="mt-8 text-sm text-text-muted">ผู้เขียน {book.author_name}</p>
              )}

              {/* ราคา + ปุ่มซื้อ อยู่กลุ่มเดียวกัน */}
              <div className="mt-6 max-w-xl border-t border-border pt-5">
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-display text-3xl font-bold text-text sm:text-4xl">฿{book.price.toLocaleString()}</span>
                  {book.original_price && <span className="text-base text-text-muted line-through">฿{book.original_price.toLocaleString()}</span>}
                  {discount && <span className="rounded-sm bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">-{discount}%</span>}
                </div>
                {book.in_stock ? (
                  <>
                    <button
                      onClick={buy}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-indigo-500 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(99,102,241,0.3)] transition hover:bg-violet-400"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      สั่งซื้อสินค้า
                    </button>
                    <button
                      onClick={inCart ? () => navigate('/cart') : addToCart}
                      className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-full border border-indigo-300 bg-white py-3 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      {inCart ? 'อยู่ในตะกร้าแล้ว · ดูตะกร้า' : 'เพิ่มลงตะกร้า'}
                    </button>
                    {cartNotice && <p className="mt-2 text-xs text-emerald-600">{cartNotice}</p>}
                    <p className="mt-2.5 text-[11px] text-text-muted">เลือกรูปแบบหนังสือ จำนวนเล่ม และกรอกที่อยู่จัดส่งในขั้นตอนถัดไป</p>
                  </>
                ) : (
                  <>
                    <button
                      disabled
                      className="mt-4 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-gray-200 py-3 text-sm font-semibold text-gray-500"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      สินค้าหมด
                    </button>
                    <p className="mt-2.5 text-[11px] text-text-muted">หนังสือเล่มนี้หมดชั่วคราว กรุณากลับมาดูใหม่อีกครั้ง</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* รายละเอียด */}
      {book.detail && <DetailSections detail={book.detail} book={book} />}
    </div>
  )
}

// ── ส่วนรายละเอียด (template เดียวกันทุกเล่ม — ข้อความทั้งหมดมาจาก BookDetailContent) ─────────
const pad2 = (n: number) => String(n).padStart(2, '0')

function DetailSections({ detail, book }: { detail: BookDetailContent; book: ApiBookDetail }) {
  // ส่วนที่แอดมินไม่ได้กรอก (list ว่าง) ไม่แสดง section นั้น
  const hasProblems = detail.problems.length > 0
  const hasAbout = detail.about.length > 0
  const hasInclusions = detail.inclusions.length > 0
  const hasChapters = detail.chapters.length > 0
  const hasAudience = detail.audience.groups.length > 0

  return (
    <>
      {/* 1) ปัญหา — พื้นเข้ม รายการเลขใหญ่แบบนิตยสาร */}
      {hasProblems && (
        <section className="relative overflow-hidden bg-[#1C1A33] py-16 md:py-24 text-white">
          <div className="pointer-events-none absolute -right-32 top-0 h-[360px] w-[360px] rounded-full bg-accent/30 blur-3xl" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }}
          />
          <div className="relative mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="max-w-2xl font-display text-3xl font-bold leading-snug sm:text-[34px]">คุณกำลังเจอปัญหาเหล่านี้อยู่หรือเปล่า</h2>
            <ol className="mt-10 grid grid-cols-1 gap-x-12 md:grid-cols-2">
              {detail.problems.map((p, i) => (
                <li key={p} className="flex items-baseline gap-4 py-3.5">
                  <span className="w-9 shrink-0 font-display text-xl font-bold tabular-nums text-violet-400">{pad2(i + 1)}</span>
                  <span className="text-base leading-relaxed text-white/90">{p}</span>
                </li>
              ))}
            </ol>
            <p className="mt-10 text-lg font-semibold text-violet-200">หนังสือเล่มนี้ช่วยได้ →</p>
          </div>
        </section>
      )}

      {/* 2) เกี่ยวกับหนังสือ — ข้อความเรียงแบบบทความ */}
      {hasAbout && (
        <section className="bg-white py-16 md:py-24">
          <div className="mx-auto grid grid-cols-1 max-w-6xl gap-12 px-4 md:grid-cols-[1fr_1.4fr] md:px-6">
            <div>
              <div>
                <p className="mb-3 text-sm font-semibold tracking-wide text-accent">เกี่ยวกับหนังสือเล่มนี้</p>
                <h2 className="font-display text-3xl font-bold leading-snug text-text sm:text-[34px]">หนังสือเล่มนี้จะ<span className="text-accent">ช่วยคุณ</span>ได้อย่างไร</h2>
              </div>
            </div>
            <div className="space-y-10">
              {detail.about.map((a, i) => (
                <div key={a.title} className="flex gap-6">
                  <span className="font-display text-4xl font-bold leading-none text-accent/25">{pad2(i + 1)}</span>
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-text">{a.title}</h3>
                    <p className="max-w-prose text-base leading-8 text-text-muted">{a.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3) สิ่งที่ได้รับ — บัตรราคาพิเศษ (จากราคาหนังสือ) + รายการของแถม/เครื่องมือ */}
      <section className="bg-[#F4F2FF] py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className={`grid gap-6 ${hasInclusions ? 'lg:grid-cols-[0.9fr_1.4fr]' : 'mx-auto max-w-md'}`}>
            <div className="relative overflow-hidden rounded-md border-2 border-dashed border-violet-300 bg-gradient-to-br from-accent to-violet-600 p-8 text-white">
              <span className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border-2 border-dashed border-violet-300 bg-[#F4F2FF]" />
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-violet-200">ราคาพิเศษ</p>
              <h3 className="font-display text-xl font-bold">ราคาสั่งซื้อวันนี้</h3>
              <div className="mt-5">
                <p className="font-display text-6xl font-bold leading-none">{book.price.toLocaleString()}<span className="ml-1 text-xl font-semibold">บาท</span></p>
                {book.original_price && book.original_price > book.price && (
                  <>
                    <p className="mt-2 text-sm text-violet-100"><span className="line-through">ราคาเต็ม {book.original_price.toLocaleString()} บาท</span></p>
                    <span className="mt-4 inline-block rounded-sm bg-white/20 px-2.5 py-1 text-xs font-semibold">ประหยัด {(book.original_price - book.price).toLocaleString()} บาท</span>
                  </>
                )}
              </div>
            </div>
            {hasInclusions && (
              <div className="relative overflow-hidden rounded-md border-2 border-dashed border-accent/30 bg-white p-8 text-text">
                <span className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border-2 border-dashed border-accent/30 bg-[#F4F2FF]" />
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-accent">ในเล่ม</p>
                <h3 className="font-display text-xl font-bold">สิ่งที่คุณจะได้รับ</h3>
                <ul className="mt-5 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                  {detail.inclusions.map((it, k) => (
                    <li key={it.label} className="flex items-baseline gap-3 border-b border-border py-3 text-[15px]">
                      <span className="font-mono text-xs text-accent">{pad2(k + 1)}</span>
                      <span className="flex-1 leading-snug">{it.label}</span>
                      {it.tag && <span className="shrink-0 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">{it.tag}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 4) สารบัญ — หน้าตาเหมือนสารบัญหนังสือจริง */}
      {hasChapters && (
        <section className="bg-white py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <p className="mb-3 text-sm font-semibold tracking-wide text-accent">เปิดดูข้างในเล่ม</p>
            <h2 className="font-display text-3xl font-bold leading-snug text-text sm:text-[34px]">{detail.chapters_title || 'สารบัญ'}</h2>
            <div className="mt-10 grid grid-cols-1 gap-x-14 md:grid-cols-2">
              {detail.chapters.map((c) => (
                <div key={c.no} className="flex gap-4 border-b border-border py-4">
                  <span className="w-10 shrink-0 font-display text-2xl font-bold leading-none text-accent">{pad2(c.no)}</span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold leading-snug text-text">{c.title}</h3>
                    {c.text && <p className="mt-1.5 text-sm leading-7 text-text-muted">{c.text}</p>}
                    {c.items.length > 0 && <p className="mt-1.5 text-sm leading-7 text-text-muted">{c.items.join(' · ')}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5) เหมาะกับใคร — ซ้ายหัวข้อ+หมายเหตุ ขวารายชื่อกลุ่มผู้อ่านเป็นข้อ ๆ */}
      {hasAudience && (
        <section className="border-t border-border bg-[#F4F2FF] py-16 md:py-24">
          <div className="relative mx-auto grid grid-cols-1 max-w-6xl gap-12 px-4 md:grid-cols-[1fr_1.5fr] md:px-6">
            <div>
              <p className="mb-3 text-sm font-semibold tracking-wide text-accent">เหมาะสำหรับ</p>
              <h2 className="border-l-4 border-accent pl-4 font-display text-3xl font-bold leading-snug text-text sm:text-[34px]">{detail.audience.title || 'หนังสือเล่มนี้เหมาะกับใคร'}</h2>
            </div>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {detail.audience.groups.map((g) => (
                <li key={g} className="flex items-start gap-3 rounded-md border border-white bg-white/90 px-5 py-4 shadow-[0_4px_16px_rgba(76,63,181,0.08)]">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-white">
                    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10.5l4 4 8-9" /></svg>
                  </span>
                  <span className="text-base font-medium leading-relaxed text-text">{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  )
}
